import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/common/CTASection";
import { Icon } from "@/components/common/Icon";
import { TRUST, CASE_STUDIES, IMAGES } from "@/lib/content";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const EXPERTISE = ["GCC Setup", "Business Management", "IT Services", "Consulting", "Operations", "Legal", "Compliance", "Finance", "HR"];

export default function About() {
  const navigate = useNavigate();
  return (
    <>
      <PageHero
        kicker="About Pixel-Hatch"
        title="An experienced team building a new-generation India expansion partner."
        subtitle="We're not a legacy consultancy. We're a team that has set up capability centers, run operations, and solved the exact problems our customers face — now focused entirely on making India expansion simple."
        image={IMAGES.boardroom}
      />

      <section className="section-pad" data-testid="about-story">
        <div className="container-px grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-white/10">
              <img src={IMAGES.officeMeeting} alt="Pixel-Hatch team at work" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading kicker="Our Story" title="Built by people who've done it before." />
            <Reveal i={2}>
              <div className="space-y-5 text-zinc-400">
                <p>
                  Pixel-Hatch was founded on a simple observation: global companies love the idea of
                  building in India, but dread the complexity of actually doing it. Entity setup,
                  hiring, payroll, compliance, IT, workspace — each handled by a different vendor,
                  each with its own risk.
                </p>
                <p>
                  Our team has lived that complexity from the inside — across GCC setup, operations,
                  IT, consulting, legal, compliance, finance, and HR. We started Pixel-Hatch to bring
                  all of it under one accountable partner, guided by a clear methodology: the HATCH
                  Framework™.
                </p>
                <p>
                  We're a new venture, and we're honest about that. What we bring is genuine
                  experience, a transparent approach, and a deep commitment to getting your India
                  operation right.
                </p>
              </div>
            </Reveal>
            <Reveal i={3}>
              <div className="mt-8 flex flex-wrap gap-2">
                {EXPERTISE.map((e) => (
                  <span key={e} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs text-zinc-300">
                    {e}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="section-pad border-y border-white/10 bg-[#131316]" data-testid="trust-section">
        <div className="container-px">
          <SectionHeading
            kicker="Why Trust Us"
            title="Credibility, built honestly."
            subtitle="We're a new venture, so we won't invent client logos or awards. Instead, here's what you can genuinely count on."
            center
          />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TRUST.map((t, i) => (
              <Reveal key={t.title} i={i}>
                <div className="h-full rounded-2xl border border-white/8 bg-white/[0.02] p-7 transition-colors hover:border-orange-500/30">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/5 text-orange-400">
                    <Icon name={t.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="font-head text-lg font-bold text-white">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE CAN HELP */}
      <section className="section-pad" data-testid="how-we-help">
        <div className="container-px">
          <SectionHeading
            kicker="How We Can Help"
            title="Real scenarios, honest answers."
            subtitle="No invented success stories — just clear examples of how we support companies at different stages. Read each as a full story."
          />
          <div className="grid gap-5 md:grid-cols-3">
            {CASE_STUDIES.map((h, i) => (
              <Reveal key={h.slug} i={i}>
                <Link
                  to={`/stories/${h.slug}`}
                  data-testid={`how-we-help-card-${h.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/30"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={h.image} alt={h.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#17171A] to-transparent" />
                    <span className="absolute left-4 top-4 rounded-full border border-orange-500/20 bg-[#17171A]/80 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">{h.sector}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-head text-lg font-bold leading-snug text-white group-hover:text-orange-400">{h.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{h.teaser}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-orange-400">
                      Read the story <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal i={2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/stories" className="btn-ghost" data-testid="about-view-stories">
                View All Stories <ArrowUpRight className="h-4 w-4" />
              </Link>
              <button onClick={() => navigate("/contact")} className="btn-primary" data-testid="about-cta">
                Discuss Your Expansion <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
